import type { LocationId, NodeId, WorldDefinition } from '@/world-prototype/types';
import {
  getLocation,
  getPresenceSpace,
  getTravelHub,
  resolveTravelArrivalNodeId,
} from '@/world-prototype/graph/worldGraph';

export type NavigationReason = 'travel' | 'restore' | 'system';

export interface NavigationEvent {
  nodeId: NodeId;
  reason: NavigationReason;
}

export type NavigationListener = (event: NavigationEvent) => void;

const STORAGE_KEY_PREFIX = 'porchlight.world-prototype.node:';

/**
 * Migrate older prototype node ids into the presence world model.
 */
const STORED_NODE_MIGRATIONS: Readonly<Record<string, NodeId>> = {
  'front-walk': 'places-map',
  'front-porch': 'places-map',
  'front-porch-left': 'places-map',
  'front-porch-front-yard': 'places-map',
  'front-porch-right': 'places-map',
  'front-yard-center': 'places-map',
  'front-yard': 'places-map',
  'cul-de-sac': 'places-map',
  overhead: 'places-map',
  'back-porch': 'hubert',
  'back-porch-left': 'hubert',
  'back-porch-backyard': 'hubert',
  'back-porch-right': 'hubert',
  'backyard-center': 'hubert',
  backyard: 'hubert',
};

function migrateStoredNodeId(id: string | null): NodeId | null {
  if (!id) return null;
  return STORED_NODE_MIGRATIONS[id] ?? id;
}

/**
 * Travel System — changes the current presence space or travel hub.
 * Walking lives entirely inside PresenceController.
 */
export class WorldNavigator {
  private nodeId: NodeId;
  private readonly world: WorldDefinition;
  private readonly listeners = new Set<NavigationListener>();
  private locked = false;
  private readonly storageKey: string;

  constructor(world: WorldDefinition, startId?: NodeId) {
    this.world = world;
    this.storageKey = `${STORAGE_KEY_PREFIX}${world.id}`;
    const restored = migrateStoredNodeId(
      startId ?? readStoredNodeId(this.storageKey),
    );
    const initial =
      restored && isValidNode(world, restored) ? restored : world.startId;
    this.nodeId = initial;
    // Persist + mirror into ?node= so a later Save/HMR reload restores here.
    writeStoredNodeId(this.storageKey, initial);
    syncNodeSearchParam(initial);
  }

  getWorld(): WorldDefinition {
    return this.world;
  }

  getCurrentId(): NodeId {
    return this.nodeId;
  }

  getCurrentLocationId(): LocationId {
    const hub = getTravelHub(this.world, this.nodeId);
    if (hub) return hub.locationId;
    return this.nodeId;
  }

  isPresence(): boolean {
    return Boolean(getPresenceSpace(this.world, this.getCurrentLocationId()));
  }

  getCurrentSpace() {
    return getPresenceSpace(this.world, this.getCurrentLocationId());
  }

  getCurrentHub() {
    return getTravelHub(this.world, this.nodeId);
  }

  isLocked(): boolean {
    return this.locked;
  }

  setLocked(locked: boolean): void {
    this.locked = locked;
  }

  canTravelToLocation(locationId: LocationId): boolean {
    getLocation(this.world, locationId);
    const space = this.getCurrentSpace();
    if (space?.utilityTravel.some((u) => u.targetLocationId === locationId)) {
      return true;
    }
    const hub = this.getCurrentHub();
    if (hub?.utilityTravel.some((u) => u.targetLocationId === locationId)) {
      return true;
    }
    return false;
  }

  travelToLocation(locationId: LocationId): boolean {
    if (!this.canTravelToLocation(locationId)) return false;
    const arrivalNodeId = resolveTravelArrivalNodeId(this.world, locationId);
    return this.commit(arrivalNodeId, 'travel');
  }

  activateUtilityTravel(utilityId: string): boolean {
    if (this.locked) return false;
    const space = this.getCurrentSpace();
    const fromSpace = space?.utilityTravel.find((u) => u.id === utilityId);
    if (fromSpace) {
      return this.travelToLocation(fromSpace.targetLocationId);
    }
    const hub = this.getCurrentHub();
    const fromHub = hub?.utilityTravel.find((u) => u.id === utilityId);
    if (fromHub) {
      return this.travelToLocation(fromHub.targetLocationId);
    }
    return false;
  }

  subscribe(listener: NavigationListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private commit(targetId: NodeId, reason: NavigationReason): boolean {
    if (this.locked) return false;
    if (targetId === this.nodeId) return false;
    if (!isValidNode(this.world, targetId)) {
      throw new Error(`Unknown node "${targetId}"`);
    }
    this.nodeId = targetId;
    writeStoredNodeId(this.storageKey, targetId);
    syncNodeSearchParam(targetId);
    const event: NavigationEvent = { nodeId: targetId, reason };
    for (const listener of this.listeners) {
      listener(event);
    }
    return true;
  }
}

/** Keep ?node= in sync (edit mode) so Vite reloads after Save stay in place. */
function syncNodeSearchParam(nodeId: NodeId): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    // Only mirror into the URL while World Editor is open — keeps the live
    // site home URL clean.
    if (!url.searchParams.has('edit')) return;
    if (url.searchParams.get('node') === nodeId) return;
    url.searchParams.set('node', nodeId);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* ignore */
  }
}

function isValidNode(world: WorldDefinition, id: NodeId): boolean {
  return Boolean(world.spaces[id] || world.hubs[id]);
}

function readStoredNodeId(key: string): NodeId | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredNodeId(key: string, id: NodeId): void {
  try {
    window.localStorage.setItem(key, id);
  } catch {
    // ignore
  }
}

export function clearStoredNode(worldId: string): void {
  try {
    window.localStorage.removeItem(`${STORAGE_KEY_PREFIX}${worldId}`);
  } catch {
    // ignore
  }
}
